<template>
  <div :id="task.caseId || task._id || task.id" :style="{ borderLeftColor: statusColor }"
    class="bg-gray-50 rounded-lg shadow-sm mb-3 border-l-8 border-y-0 border-r-0 border-solid p-3.5 text-gray-800 transition-all hover:shadow-md">
    <!-- Card Header: Title & Status Badge + Action Menu -->
    <div class="flex items-center justify-between gap-2 pb-2">
      <div class="flex items-center gap-2 min-w-0">
        <h4 class="text-sm font-bold text-gray-900 mb-0 truncate tracking-tight">
          {{ taskTitle }}
        </h4>
      </div>

      <div class="flex items-center justify-end gap-1.5 shrink-0">
        <span class="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide select-none uppercase"
          :style="{ backgroundColor: statusBgColor, color: statusTextColor }">
          {{ taskStatusText }}
        </span>

        <!-- Info 'i' circular FAB with notification badge -->
        <button type="button"
          class="relative w-6 h-6 rounded-full bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-none cursor-pointer transition-all active:scale-95 shadow-2xs select-none"
          :title="`${historyCount} visit(s) recorded. Click to view history`" @click.stop="$emit('view-history', task)">
          <v-icon size="13" color="indigo darken-2">mdi-information-variant</v-icon>
          <span v-if="historyCount > 0"
            class="absolute -top-1 -right-1 bg-indigo-600 text-white rounded-full min-w-[13px] h-[13px] p-1 font-bold flex items-center justify-center text-[10px] font-black leading-none shadow-xs border border-white">
            {{ historyCount }}
          </span>
        </button>
      </div>
    </div>

    <!-- Optional description -->
    <p v-if="task.description" class="text-xs text-gray-500 mb-2 line-clamp-2">
      {{ task.description }}
    </p>

    <!-- Key-Value Dotted Information Rows exactly matching FinFi App -->
    <div v-if="taskInfos.length" class="space-y-1 py-1">
      <template v-for="(info, index) in visibleInfos">
        <div :key="info.label + index"
          class="flex items-center justify-between text-xs py-1 border-b border-dashed border-gray-200">
          <span class="text-gray-500 font-normal shrink-0 mr-2">{{ info.label }}</span>
          <span class="font-semibold text-gray-800 text-right truncate max-w-[65%]" :class="info.class">
            {{ info.prefix }} {{ info.value }} {{ info.suffix }}
          </span>
        </div>
      </template>

      <!-- View More / View Less Toggle -->
      <div v-if="taskInfos.length > 4" @click="isExpanded = !isExpanded"
        class="pt-1.5 font-bold flex items-center justify-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 cursor-pointer select-none">
        <span>{{ isExpanded ? 'View Less Info' : 'View More Info' }}</span>
        <v-icon x-small color="blue darken-1">{{ isExpanded ? 'mdi-chevron-up-circle' : 'mdi-chevron-down-circle'
          }}</v-icon>
      </div>
    </div>

    <!-- Card Footer matching App buttons -->
    <div class="flex items-center justify-between pt-2.5 mt-1 border-t border-gray-100">
      <div class="text-xs text-gray-500 font-medium flex items-center gap-1">
        <v-icon x-small color="grey">mdi-clock-outline</v-icon>
        <span>{{ displayDate }}</span>
      </div>

      <div class="flex items-center justify-end gap-2 shrink-0">
        <!-- Call Button -->
        <v-btn v-if="customerMobile" icon small :color="$vuetify.theme.currentTheme.employeePrimary" class="bg-indigo-50 rounded-full"
          :href="'tel:' + customerMobile">
          <v-icon small :color="$vuetify.theme.currentTheme.employeePrimary">mdi-phone</v-icon>
        </v-btn>

        <!-- Continue / Start Job Button -->
        <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary"
          class="white--text rounded-lg text-xs font-bold px-3.5 h-8 capitalize tracking-normal"
          @click="$emit('view-details', task)">
          <span>Continue</span>
          <v-icon right x-small class="ml-1">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { convertNumberToAmount } from '@/app/utils/helpers'

export default {
  name: 'AppTaskCard',
  props: {
    task: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isExpanded: false
    }
  },
  computed: {
    taskTitle() {
      if (!this.task) return 'Task'
      if (this.task.title) return this.task.title
      if (this.task.caseNo) return `${this.task.caseNo}`
      if (this.task.leadDetails?.leadTitle) return this.task.leadDetails.leadTitle
      return this.task.taskTitle || 'Task'
    },
    customerName() {
      if (!this.task) return ''
      return (
        this.task.customer?.customerFullName ||
        this.task.customer?.name ||
        (this.task.customer?.firstName ? `${this.task.customer.firstName} ${this.task.customer.lastName || ''}`.trim() : '') ||
        this.task.leadDetails?.company?.companyBranches?.[0]?.alias ||
        ''
      )
    },
    customerMobile() {
      if (!this.task) return ''
      const mob = this.task.customer?.customerMobile || this.task.customer?.mobile
      if (Array.isArray(mob)) return mob[0]
      return mob || ''
    },
    taskStatusText() {
      if (!this.task) return 'OPEN'
      return (this.task.isCollection ? this.task.caseStatus : this.task.status) || 'OPEN'
    },
    historyCount() {
      if (!this.task) return 1
      const taskList = this.task.tasks || []
      if (Array.isArray(taskList) && taskList.length > 0) {
        return taskList.length
      }
      return 1
    },
    completedVisitsCount() {
      if (!this.task) return 0
      const taskList = this.task.tasks || []
      if (Array.isArray(taskList) && taskList.length > 0) {
        return taskList.filter((t) => (t.taskStatus || t.status) === 'COMPLETED').length
      }
      return (this.taskStatusText || '').toUpperCase() === 'COMPLETED' ? 1 : 0
    },
    statusColor() {
      const s = (this.taskStatusText || '').toUpperCase()
      const colors = {
        PENDING: '#FFCC00',
        OPEN: '#3399FF',
        MISSED: '#FF0000',
        IN_PROGRESS: '#3399FF',
        ENDED: '#66CC66',
        DEFERRED: '#FF9900',
        COMPLETED: '#00CC00',
        CLOSED: '#00CC00',
        CANCELLED: '#999999',
        REJECTED: '#FF3366',
        APPROVED: '#33CC33'
      }
      return colors[s] || '#3399FF'
    },
    statusBgColor() {
      const s = (this.taskStatusText || '').toUpperCase()
      if (s === 'COMPLETED' || s === 'CLOSED' || s === 'APPROVED') return '#ECFDF5'
      if (s === 'IN_PROGRESS' || s === 'OPEN') return '#EFF6FF'
      if (s === 'MISSED' || s === 'REJECTED') return '#FEF2F2'
      return '#FFFBEB'
    },
    statusTextColor() {
      const s = (this.taskStatusText || '').toUpperCase()
      if (s === 'COMPLETED' || s === 'CLOSED' || s === 'APPROVED') return '#047857'
      if (s === 'IN_PROGRESS' || s === 'OPEN') return '#1D4ED8'
      if (s === 'MISSED' || s === 'REJECTED') return '#B91C1C'
      return '#B45309'
    },
    displayDate() {
      const d = this.task.caseStartDate || this.task.createdAt || this.task.taskStartDateTime || this.task.startDate
      return d ? dayjs(d).format('DD MMM, YYYY') : '-'
    },
    taskInfos() {
      const infos = []
      const t = this.task

      // 1. Customer Name
      if (this.customerName) {
        infos.push({ label: 'Customer Name', value: this.customerName })
      }

      // 2. Account Number / Case No
      if (t.caseNo) {
        infos.push({ label: 'Case Number', value: `#${t.caseNo}` })
      } else if (t.leadDetails?.leadTitle) {
        infos.push({ label: 'Account Number', value: t.leadDetails.leadTitle })
      }

      // 3. Principal Outstanding / POS Amount
      const pos = t.pos || t.collectionDetails?.principalOutstanding || t.currentBalance
      if (pos) {
        infos.push({
          label: 'POS Amount',
          value: `₹ ${convertNumberToAmount(pos)}`,
          class: 'text-indigo-700 font-bold'
        })
      }

      // 4. EMI Amount
      const emi = t.emiAmount || t.collectionDetails?.emiAmount
      if (emi) {
        infos.push({
          label: 'EMI Amount',
          value: `₹ ${convertNumberToAmount(emi)}`,
          class: 'text-teal-700 font-bold'
        })
      }

      // 5. Bucket
      const bucket = t.bucket || t.collectionDetails?.bucket
      if (bucket) {
        infos.push({ label: 'Bucket', value: typeof bucket === 'object' ? bucket.name || bucket.label : bucket })
      }

      // 6. Case Type / SubCase
      if (t.caseType || t.subCase) {
        infos.push({ label: 'Case Type', value: t.caseType || t.subCase })
      }

      // 7. Tenure
      const tenure = t.tenure || t.collectionDetails?.tenure
      if (tenure) {
        infos.push({ label: 'Tenure', value: `${tenure} Months` })
      }

      // 8. Meeting Outcome
      const meetingOutcome = t.meetingDetails?.meetingOutCome || t.userDefinedCaseStatus
      if (meetingOutcome) {
        infos.push({ label: 'Visit Outcome', value: meetingOutcome })
      }

      // 9. Fund Collected
      const fund = t.meetingDetails?.fundCollected
      if (fund) {
        infos.push({ label: 'Fund Collected', value: `₹ ${convertNumberToAmount(fund)}`, class: 'text-emerald-700 font-bold' })
      }

      // 10. Address
      const addrList = t.customer?.customerAddress
      if (Array.isArray(addrList) && addrList.length > 0) {
        const a = addrList[0]
        const str = [a.addressLine1, a.city, a.state, a.pinCode].filter(Boolean).join(', ')
        if (str) infos.push({ label: 'Address', value: str })
      }

      return infos
    },
    visibleInfos() {
      if (this.isExpanded || this.taskInfos.length <= 4) {
        return this.taskInfos
      }
      return this.taskInfos.slice(0, 4)
    }
  }
}
</script>
